import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useT } from '../i18n';

/* ═══════════════════════════════════════════════════════════════
   PricingSection — KOMPONEN HARGA DIKONGSI (satu sumber, auto-sync)
   ───────────────────────────────────────────────────────────────
   Dipakai oleh:
     • PricingBaruView  (/pricing-baru)  — salespage penuh
     • SalesPageView    (/sesaat-1)      — section "Harga mudah…"
     • StartGate                          — gate langganan (login)
   Edit sini SEKALI → ketiga-tiga tempat ikut.

   Kandungan: 2 kad pakej (Premium hitam popular + Fighter putih;
   mobile = swipe carousel sebelah-sebelah, kad ke-2 menjenguk) +
   table perbandingan peek + fade (desktop & mobile) +
   butang "Bandingkan semua pakej".

   Props:
     onSubscribe(planKey) — dipanggil bila klik CTA.
                            planKey = 'premium' | 'fighter' | 'flownity_addon' | 'premium_renew_flownity'
     payLoading           — bool; disable butang semasa proses bayaran
     fadeColor            — hex bg host untuk gradient fade peek (default cream)
     viewer               — { loggedIn, tier, plan, licenseStatus, allowedModules }
                            (optional — tanpa viewer, render sama macam public/anon)
   ═══════════════════════════════════════════════════════════════ */

// ═══ LOCK butang Langgan — pause SEMUA butang subscribe (pricing + salespage /sesaat-1 + StartGate).
//     UNLOCKED 2026-07-03 (F5) — FLOWNITY addon + smart-button dah live. Set `true` untuk pause jualan semula. ═══
const LOCKED = false;
// ⚠️ Teks label WAJIB dihantar masuk oleh pemanggil (daripada kamus). Dulu ia
//    ada nilai lalai 'Langgan' + pemalar `LOCK_LABEL` aras-modul; kedua-duanya
//    dinilai sekali masa fail dimuatkan, jadi ia tidak boleh ikut bahasa yang
//    dipilih pelanggan. Setiap komponen kini membina labelnya sendiri.
const lockLabel = (text) => (
    <span className="inline-flex items-center gap-1.5">
        <span className="material-symbols-outlined text-[15px]">lock</span> {text}
    </span>
);

// ═══ 2 pakej ═══
//     Dibina pada masa render (bukan pemalar aras-modul) sebab labelnya datang
//     daripada kamus — pemalar di luar komponen tidak boleh memanggil useT().
//     Nama pakej, harga dan planKey KEKAL literal: itu identiti produk + data
//     yang pergi ke server, bukan ayat yang diterjemah.
// ⭐ DUA PAKEJ (2026-08-26). Fighter BUKAN kad ketiga — ia keadaan TAHUNAN bagi kad
//    Premium (toggle di dalam kad). Fighter dapat semua yang Premium dapat; bezanya
//    6 browser + bayaran setahun. Sebab itu ia berkongsi satu kad: tiga kotak
//    membuat pelanggan membandingkan tiga benda sedangkan pilihannya cuma dua.
// ⛔ planKey = data yang pergi ke server (BILLPLZ_PLANS). Nama pakej, harga dan
//    planKey KEKAL literal — itu identiti produk, bukan ayat yang diterjemah.
const buildTiers = (t, yearly, headerExtra) => [
    { id: 'smart', name: 'SMART', sub: t('pricing.tier.smart.sub'), price: 'RM99', period: t('pricing.perMonth'), popular: false, cta: t('pricing.cta.subscribeSmart'), planKey: 'smart',
      priceNote: t('pricing.tier.smart.priceNote'),
      video: { id: '571yXv6V1Uw', label: t('pricing.tier.smart.demo') },
      highlights: [
          t('pricing.tier.smart.h1'),
          t('pricing.tier.smart.h7'),
          t('pricing.tier.premium.p4'),
          t('pricing.tier.browser3'),
          t('pricing.tier.smart.h3'),
          t('pricing.tier.smart.h4'),
          t('pricing.tier.smart.h5'),
          t('pricing.tier.smart.h6'),
      ] },
    { id: 'pro', name: yearly ? 'Fighter' : 'Premium', sub: t('pricing.tier.premium.subNew'),
      price: yearly ? 'RM799' : 'RM199',
      period: yearly ? t('pricing.tier.fighter.period') : t('pricing.tier.premium.period'),
      oldPrice: yearly ? 'RM1188' : undefined,
      popular: true, cta: t('pricing.cta.subscribe'), planKey: yearly ? 'fighter' : 'premium',
      priceNote: yearly ? t('pricing.tier.fighter.priceNote') : t('pricing.tier.premium.priceNote'),
      video: { id: 'LRv5LOwMRuI', label: t('pricing.tier.premium.demo') },
      headerExtra,
      highlights: [
          t('pricing.tier.premium.p1'),
          t('pricing.tier.premium.p2'),
          t('pricing.tier.premium.p3'),
          t('pricing.tier.premium.p4'),
          yearly ? t('pricing.tier.browser6') : t('pricing.tier.browser3'),
          t('pricing.tier.smart.h3'),
          t('pricing.tier.smart.h4'),
          t('pricing.tier.smart.h5'),
          t('pricing.tier.smart.h6'),
      ] },
];

// ═══ Baris perbandingan. val: true=tick, false=dash, string=nilai ═══
//     Sama seperti buildTiers: dibina masa render supaya label ikut bahasa.
//     Nama modul + terma teknikal (Flow automation, Import Product) kekal
//     English dalam kedua-dua bahasa, jadi ia tidak melalui kamus.
const buildSections = (t, yearly) => [
    {
        // Lajur: [SMART, Premium/Fighter]. Sejak 29 Ogos 2026 Premium DAN Fighter = SMART +
        // AI8SAAT + GOYANG + AI16SAAT + HOME NICHE (sama module; beza browser + tempoh).
        // FLOWNITY TIADA dalam pakej. (Nota lama 2026-08-03 "Premium = modul teras, GOYANG/SMART
        // eksklusif Fighter" sudah BASI — nota itu diikut semasa sistem bayaran diubah, dan
        // deploy 16 Sept memberi pembeli Premium baharu 3 module sahaja. Dibaiki 18 Sept.)
        // ⚠️ Ini PAPARAN/janji jualan. Grant sebenar ditentukan server (BILLPLZ_COMBO_MODULES
        //    dalam functions/index.js) + popup Aktifkan admin (PREMIUM_DEFAULT_MODULES dalam
        //    AdminView.jsx — ⚠️ 18 Sept masih lalai 3 module, BELUM diselaraskan).
        //    Ubah baris di sini = ubah tempat-tempat itu SERENTAK.
        title: t('pricing.section.modules'), rows: [
            { label: 'SMART',        vals: [true,  true] },
            { label: 'AI8SAAT',      vals: [t('pricing.val.pilihSatu'), true] },
            { label: 'GOYANG',       vals: [t('pricing.val.pilihSatu'), true] },
            { label: 'AI16SAAT',     vals: [false, true] },
            { label: 'HOME NICHE',   vals: [false, true] },
        ],
    },
    {
        title: t('pricing.section.usage'), rows: [
            // Baris pertama = beza sebenar antara dua pakej: AI siapa yang menulis.
            { label: t('pricing.row.aiIncluded'),         vals: [t('pricing.val.ownGptPlus'), true] },
            { label: t('pricing.row.generationsPerDay'),  vals: ['100',  '100'] },
            { label: t('pricing.row.videoOutputPerDay'),  vals: ['≈300', '≈300'] },
            { label: t('pricing.row.cloudVideoPerDay'),   vals: ['300',  '300'] },
            { label: t('pricing.row.browserPerAccount'),  vals: ['3',    yearly ? '6' : '3'] },
        ],
    },
    {
        title: t('pricing.section.toolsSupport'), rows: [
            { label: 'Flow automation',           vals: [true,  true] },
            { label: 'TikTok auto-upload',        vals: [true,  true] },
            { label: 'Import Product',            vals: [true,  true] },
            { label: t('pricing.row.ownFormula'), vals: [false, true] },
            { label: 'Flow Ultra (Sharing)',      vals: [t('pricing.val.notAvailable'), 'Add-on +RM100'] },
            { label: t('pricing.row.support'),    vals: [t('pricing.val.whatsappGroup'), t('pricing.val.whatsappGroup')] },
        ],
    },
];

// ═══ Kotak demo video — pautan sahaja, TIADA auto play ═══
//     ⛔ Arahan user 2026-08-26: JANGAN benamkan pemain yang bermain sendiri.
//        Video hanya bermula selepas pelanggan menekannya, dan ia dibuka di tab baharu
//        supaya halaman harga (dan tick add-on) tidak hilang di belakangnya.
//     Kedudukan: DI ATAS harga. Pelanggan perlu tahu ini benda apa sebelum dia
//     melihat nombor, bukan selepas dia sudah membuat keputusan.
function VideoBox({ video }) {
    const [main, setMain] = useState(false);

    // Ditekan → pemain YouTube menggantikan gambar DALAM kad yang sama.
    // autoplay=1 di sini TIDAK melanggar arahan "jangan auto play": ia hanya
    // dimuatkan selepas pelanggan menekan, jadi tiada video bermula sendiri
    // semasa halaman dibuka. Pemain hanya dimuatkan pada ketika ini juga —
    // halaman harga tidak menanggung berat YouTube untuk orang yang tak tekan.
    if (main) {
        return (
            <div className="rounded-xl overflow-hidden mb-4 aspect-video bg-black">
                <iframe
                    src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
                    title={video.label}
                    className="w-full h-full"
                    style={{ border: 0 }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            </div>
        );
    }

    // ⛔ Gambar SEBENAR video, bukan petak kosong. Versi pertama (26 Ogos) memakai
    //    petak kelabu berikon sahaja — pada skrin sebenar ia dibaca sebagai kotak
    //    rosak, bukan video. Jangan pulangkan rupa itu.
    //    maxresdefault tidak wujud untuk setiap video → jatuh ke hqdefault.
    return (
        <button
            type="button"
            onClick={() => setMain(true)}
            aria-label={video.label}
            className="relative block w-full rounded-xl overflow-hidden mb-4 aspect-video group bg-black"
        >
            <img
                src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`; }}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
            />
            <span className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white/95 shadow-lg">
                    <span className="material-symbols-outlined text-[26px] text-[#14130f]" style={{ marginLeft: 2 }}>play_arrow</span>
                </span>
                <span className="text-[12px] font-bold text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,.6)' }}>{video.label}</span>
            </span>
        </button>
    );
}

// ═══ Toggle Bulanan / Tahunan — dalam kad Premium sahaja ═══
//     Ia menukar planKey yang pergi ke server ('premium' ↔ 'fighter'), harga, tempoh,
//     DAN baris browser (3 ↔ 6). ⛔ Kalau kelak ada beza lain antara Premium dan
//     Fighter, ia WAJIB ditambah pada senarai highlights dalam buildTiers juga —
//     kalau tidak toggle ini menjanjikan sesuatu yang tidak berubah.
function PeriodToggle({ yearly, onChange, disabled }) {
    const t = useT();
    const tab = (on) => `flex-1 h-8 rounded-lg text-[12px] font-bold transition-colors disabled:cursor-not-allowed ${
        on ? 'bg-white text-[#14130f]' : 'text-white/60 hover:text-white/85'
    }`;
    return (
        <div className="flex gap-1 p-1 mb-4 rounded-xl bg-white/[0.07] border border-white/10">
            <button type="button" disabled={disabled} onClick={() => onChange(false)} className={tab(!yearly)}>
                {t('pricing.toggle.monthly')}
            </button>
            <button type="button" disabled={disabled} onClick={() => onChange(true)} className={tab(yearly)}>
                {t('pricing.toggle.yearly')}
            </button>
        </div>
    );
}

// ═══ Kotak bonus — satu kotak setiap bonus (arahan user 2026-08-26) ═══
function BonusBox({ items }) {
    const t = useT();
    return (
        <div className="mb-4 pt-4 border-t border-white/10">
            <p className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-2">{t('pricing.bonus.title')}</p>
            <div className="space-y-2">
                {items.map((b) => {
                    const teks = typeof b === 'string' ? b : b.teks;
                    const href = typeof b === 'string' ? null : b.href;
                    const logo = typeof b === 'string' ? null : b.logo;
                    return (
                        <div key={teks} className="flex items-start gap-2.5 rounded-xl bg-white/[0.06] px-3.5 py-2.5">
                            {/* Logo sebenar kalau ada; kalau failnya hilang ia disorok dan
                                ikon hadiah mengambil tempatnya — bukan ikon gambar rosak. */}
                            {logo
                                ? <img src={logo} alt="" className="w-7 h-7 rounded object-contain shrink-0 bg-white/10"
                                       onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                : <span className="material-symbols-outlined text-[16px] text-[#bef264] shrink-0" style={{ marginTop: '1px' }}>redeem</span>}
                            <span className="text-[12px] text-white/85 leading-snug">
                                {teks}
                                {href && (
                                    <>
                                        {' '}
                                        <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-0.5 font-bold text-[#bef264] underline whitespace-nowrap"
                                        >
                                            {t('pricing.flowUltra.bonusCta')}
                                            <span className="material-symbols-outlined text-[12px]">open_in_new</span>
                                        </a>
                                    </>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ═══ Pakej SMART: pilih SATU module tambahan (arahan user 2026-08-27) ═══
//     ⛔ TIADA pilihan lalai. Lalai bermakna kita memilih bagi pihak pelanggan,
//        dan dia hanya perasan selepas membayar — corak yang sama sudah ditolak
//        pada tetapan Extended ChatGPT. Butang Langgan kekal MATI sehingga dipilih.
//     Senarai ini WAJIB sepadan dengan MODUL_SMART_PILIHAN di server; server
//     menolak apa-apa nama lain, jadi menambah di sini sahaja tidak mencukupi.
function PilihModulSmart({ value, onChange, bacaSahaja = false, milikSaya = false }) {
    const t = useT();
    const pilihan = [
        { id: 'ai8saat', nama: 'AI8SAAT', nota: t('pricing.smartPick.ai8saat') },
        { id: 'goyang', nama: 'GOYANG', nota: t('pricing.smartPick.goyang') },
    ];
    // Ayat berubah ikut siapa yang membaca — kotak yang sama tidak boleh berkata
    // "pilih satu" kepada orang yang memang tidak boleh memilih apa-apa di sini.
    const ayat = bacaSahaja
        ? (milikSaya ? t('pricing.smartPick.semasa') : t('pricing.smartPick.info'))
        : t('pricing.smartPick.sub');
    return (
        <div className="mb-4 pt-4 border-t border-[#e6e3db]">
            <p className="text-[12px] font-bold text-[#14130f] mb-0.5">{t('pricing.smartPick.title')}</p>
            <p className="text-[11px] text-[#6b6a64] leading-snug mb-2.5">{ayat}</p>
            <div className="grid grid-cols-2 gap-2">
                {pilihan.map((o) => {
                    const on = value === o.id;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            disabled={bacaSahaja}
                            onClick={() => { if (!bacaSahaja) onChange(o.id); }}
                            className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? 'border-[#14130f] bg-[#faf9f5]' : 'border-[#e6e3db] bg-white'} ${bacaSahaja ? 'cursor-default' : 'hover:border-[#c9c6bd]'}`}
                        >
                            <span className="flex items-center gap-1.5 mb-0.5">
                                <span className={`w-[15px] h-[15px] rounded-full border-2 flex items-center justify-center shrink-0 ${on ? 'border-[#14130f]' : 'border-[#c9c6bd]'}`}>
                                    {on && <span className="w-[7px] h-[7px] rounded-full bg-[#14130f]" />}
                                </span>
                                <span className="text-[12px] font-extrabold text-[#14130f]">{o.nama}</span>
                            </span>
                            <span className="block text-[10.5px] leading-snug text-[#6b6a64]">{o.nota}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ═══ Sel nilai (table desktop) ═══
function Check() {
    return (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#cfccc2] text-[#14130f]">
            <span className="material-symbols-outlined text-[14px]">check</span>
        </span>
    );
}
function ValCell({ v }) {
    if (v === true) return <Check />;
    if (v === false) return <span className="text-base text-[#c9c6bd]">—</span>;
    return <span className="text-sm text-[#6b6a64]">{v}</span>;
}

// ═══ Header kad (desktop + mobile) ═══
function CardHeader({ tier }) {
    const dark = tier.popular;
    return (
        <>
            <div className="mb-1">
                <span className={`text-sm font-bold ${dark ? 'text-white' : 'text-[#14130f]'}`}>{tier.name}</span>
            </div>
            {tier.sub && (
                <p className={`text-[12px] leading-snug mb-3 ${dark ? 'text-white/60' : 'text-[#6b6a64]'}`}>{tier.sub}</p>
            )}
            {tier.video && <VideoBox video={tier.video} />}
            {tier.headerExtra}
            <div className="mb-5">
                {tier.oldPrice && (
                    <div className={`text-sm line-through mb-0.5 ${dark ? 'text-white/45' : 'text-[#c2bfb5]'}`}>{tier.oldPrice}</div>
                )}
                <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className={`text-2xl font-extrabold ${dark ? 'text-white' : 'text-[#14130f]'}`}>{tier.price}</span>
                    {tier.period && <span className={`text-xs ${dark ? 'text-white/60' : 'text-[#6b6a64]'}`}>{tier.period}</span>}
                </div>
                {tier.priceNote && <p className={`text-[11px] mt-1.5 leading-snug ${dark ? 'text-white/50' : 'text-[#9c9a92]'}`}>{tier.priceNote}</p>}
            </div>
        </>
    );
}

function CardCTA({ tier, onSubscribe, className = '', label, disabled, neutral }) {
    const t = useT();
    const dark = tier.popular;
    // neutral = "Pelan Semasa" — kekal jelas dibaca (bukan lock, bukan pudar opacity-50)
    const skin = neutral
        ? (dark
            ? 'bg-white/10 border border-white/15 text-white/60 cursor-default'
            : 'bg-[#f4f3ef] border border-[#e6e3db] text-[#9c9a92] cursor-default')
        : `disabled:opacity-50 disabled:cursor-not-allowed ${
            dark
                ? 'bg-[#bef264] hover:bg-[#d9f99d] text-[#14130f]'
                : 'border border-[#e6e3db] text-[#14130f] bg-white hover:bg-[#faf9f5]'
        }`;
    return (
        <button
            onClick={onSubscribe}
            disabled={disabled}
            className={`w-full h-9 rounded-xl text-sm font-bold transition-colors ${skin} ${className}`}
        >
            {label || tier.cta || t('pricing.cta.subscribe')}
        </button>
    );
}

// ═══ Extra Premium: Add-on Flow Ultra (Sharing) +RM100 — kotak putih checkbox (pelanggan BARU sahaja) ═══
function FlowUltraAddonBox({ checked, onToggle, bonus }) {
    const t = useT();
    return (
        <div className="mb-4">
            <label className={`block cursor-pointer rounded-2xl overflow-hidden bg-white border transition-colors ${checked ? 'border-[#bef264]' : 'border-[#e6e3db]'}`}>
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-[#eceae3]">
                    <input type="checkbox" checked={checked} onChange={onToggle} className="w-4 h-4 shrink-0 accent-[#14130f] cursor-pointer" />
                    <span className="flex-1 text-[12.5px] font-bold text-[#14130f] leading-snug">Add-on: Flow Ultra (Sharing)</span>
                    <span className="text-[11px] font-bold text-[#14130f] whitespace-nowrap shrink-0">+RM100<span className="font-medium text-[#9c9a92]">{t('pricing.perMonth')}</span></span>
                </div>
                <div className="px-3.5 py-2.5 space-y-1.5">
                    <div className="flex items-start gap-2 text-[11.5px] leading-snug text-[#44433e]">
                        <span className="material-symbols-outlined text-[14px] text-[#639922] shrink-0" style={{ marginTop: '1px' }}>check</span>
                        <span>{t('pricing.flowUltra.b1')}</span>
                    </div>
                    <div className="flex items-start gap-2 text-[11.5px] leading-snug text-[#44433e]">
                        <span className="material-symbols-outlined text-[14px] text-[#639922] shrink-0" style={{ marginTop: '1px' }}>check</span>
                        <span>{t('pricing.flowUltra.b2')}</span>
                    </div>
                    <div className="flex items-start gap-2 text-[11.5px] leading-snug text-[#44433e]">
                        <span className="material-symbols-outlined text-[14px] text-[#639922] shrink-0" style={{ marginTop: '1px' }}>check</span>
                        <span>{t('pricing.flowUltra.b3')}</span>
                    </div>
                    <div className="flex items-start gap-2 text-[11.5px] leading-snug text-[#44433e]">
                        <span className="material-symbols-outlined text-[14px] text-[#639922] shrink-0" style={{ marginTop: '1px' }}>check</span>
                        <span>
                            {t('pricing.flowUltra.freeGpts')}{' '}
                            <a
                                href="https://caliphempire.bcl.my/form/tiktok-sales-sniper-2/15798"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="underline font-bold text-[#14130f]"
                            >
                                {t('pricing.flowUltra.detailLink')}
                            </a>
                        </span>
                    </div>
                    <div className="flex items-start gap-2 text-[11.5px] leading-snug text-[#993c1d]">
                        <span className="material-symbols-outlined text-[14px] shrink-0" style={{ marginTop: '1px' }}>warning</span>
                        <span>{t('pricing.flowUltra.b4')}</span>
                    </div>
                    {/* Pakej induk boleh setahun (Fighter) tetapi add-on ini KEKAL 1 bulan —
                        pelanggan mesti tahu sebelum menekan, bukan selepas membayar. */}
                    <div className="flex items-start gap-2 text-[11.5px] leading-snug text-[#993c1d]">
                        <span className="material-symbols-outlined text-[14px] shrink-0" style={{ marginTop: '1px' }}>schedule</span>
                        <span>{t('pricing.flowUltra.oneMonth')}</span>
                    </div>
                </div>
                {bonus && <BonusAwalBox bonus={bonus} />}
            </label>
        </div>
    );
}

// ═══ Bonus 50 orang terawal — dalam kotak add-on Flow Ultra ═══
//     Kiraan slot datang dari public_stats/flow_bonus (baca awam). Slot ditolak
//     oleh SERVER masa bayaran disahkan, bukan masa bil dicipta — bil yang tidak
//     dibayar tidak boleh menghabiskan slot orang lain.
//     ⛔ Bila slot habis, add-on Flow Ultra KEKAL boleh dibeli — yang tamat hanya
//        bonus. Menyorok add-on sekali bermakna kita berhenti menjual RM100 itu.
function BonusAwalBox({ bonus }) {
    const t = useT();
    const habis = bonus.tinggal <= 0;
    const produk = [
        { img: '/bonus/tss.webp', nama: 'TikTok Sales Sniper' },
        { img: '/bonus/tps.webp', nama: 'TikTok POV Sniper' },
    ];
    return (
        <div className={`px-3.5 py-3 border-t ${habis ? 'border-[#eceae3] bg-[#faf9f5]' : 'border-[#bef264]/50 bg-[#bef264]/[0.14]'}`}>
            <div className="flex items-center gap-1.5 mb-2">
                <span className={`material-symbols-outlined text-[15px] ${habis ? 'text-[#9c9a92]' : 'text-[#3B6D11]'}`}>redeem</span>
                <span className={`text-[11.5px] font-extrabold uppercase tracking-wide ${habis ? 'text-[#9c9a92]' : 'text-[#14130f]'}`}>
                    {habis ? t('pricing.flowUltra.bonusGone') : t('pricing.flowUltra.bonusTitle')}
                </span>
            </div>

            {!habis && (
                <>
                    <div className="flex items-center gap-2.5 mb-2">
                        {produk.map((p) => (
                            <span key={p.nama} className="flex items-center gap-1.5 min-w-0">
                                {/* Logo disorok kalau failnya belum dimuat naik — lebih baik
                                    daripada ikon gambar rosak pada halaman jualan. */}
                                <img
                                    src={p.img}
                                    alt=""
                                    className="w-7 h-7 rounded object-contain shrink-0"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                <span className="text-[11.5px] font-bold text-[#14130f] leading-tight">{p.nama}</span>
                            </span>
                        ))}
                    </div>

                    {/* Baki slot — satu-satunya nombor pada kotak ni, jadi ia mesti benar.
                        Ia tidak dipapar langsung kalau doc kiraan gagal dibaca. */}
                    <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-1.5 rounded-full bg-black/10 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-[#3B6D11]"
                                style={{ width: `${Math.max(4, Math.round((bonus.tinggal / bonus.jumlah) * 100))}%` }}
                            />
                        </div>
                        <span className="text-[11px] font-extrabold text-[#3B6D11] whitespace-nowrap shrink-0">
                            {t('pricing.flowUltra.bonusLeft', { baki: bonus.tinggal, jumlah: bonus.jumlah })}
                        </span>
                    </div>
                </>
            )}

            <a
                href="https://caliphempire.bcl.my/form/tiktok-sales-sniper-2/15798"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#14130f] underline"
            >
                {t('pricing.flowUltra.bonusCta')}
                <span className="material-symbols-outlined text-[13px]">open_in_new</span>
            </a>
        </div>
    );
}

// ═══ Kad pakej — DESKTOP ═══
function TierCard({ tier, onSubscribe, disabled, cta, extras, premiumLabel }) {
    const t = useT();
    const dark = tier.popular;
    // Label lalai kekal SAMA seperti dulu ('Langgan — RM199'), cuma ia kini
    // datang daripada kamus supaya ia ikut bahasa pelanggan.
    const labelPremium = premiumLabel || t('pricing.cta.subscribePremium');
    const LOCK_LABEL = lockLabel(t('pricing.cta.subscribe'));
    return (
        <div className={`relative flex flex-col h-full rounded-2xl border p-5 text-left ${dark ? 'bg-[#14130f] border-[#14130f]' : 'bg-white border-[#e6e3db]'}`}>
            {tier.popular && (
                <span className="absolute -top-2.5 right-4 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#bef264] text-[#14130f] shadow-sm">{t('pricing.badge.popular')}</span>
            )}
            <CardHeader tier={tier} />
            {tier.highlightsIntro && (
                <p className={`text-[12px] font-bold mb-3 ${dark ? 'text-white/70' : 'text-[#14130f]'}`}>{tier.highlightsIntro}</p>
            )}
            {tier.highlights && (
                <ul className="space-y-2 mb-6">
                    {tier.highlights.map((h) => (
                        <li key={h} className={`flex items-start gap-2 text-[13px] leading-snug ${dark ? 'text-white/85' : 'text-[#14130f]'}`}>
                            <span className={`material-symbols-outlined text-[16px] shrink-0 ${dark ? 'text-[#bef264]' : 'text-[#5b53e8]'}`} style={{ marginTop: '1px' }}>check</span>
                            <span>{h}</span>
                        </li>
                    ))}
                </ul>
            )}
            {extras}
            {cta ? (
                <div className="mt-auto">
                    {cta.note && <p className={`text-[11px] mb-2 leading-snug text-center ${dark ? 'text-white/60' : 'text-[#6b6a64]'}`}>{cta.note}</p>}
                    <CardCTA tier={tier} onSubscribe={cta.onClick} disabled={cta.disabled} neutral={cta.neutral} label={cta.label} />
                </div>
            ) : (
                <CardCTA tier={tier} onSubscribe={onSubscribe} disabled={disabled || LOCKED} className="mt-auto" label={LOCKED ? LOCK_LABEL : (tier.popular ? labelPremium : undefined)} />
            )}
        </div>
    );
}

// ═══ Kad pakej — MOBILE (perbandingan dalam table berasingan di bawah, gaya desktop) ═══
function MobileTierCard({ tier, onSubscribe, disabled, cta, extras, premiumLabel }) {
    const t = useT();
    const dark = tier.popular;
    const labelPremium = premiumLabel || t('pricing.cta.subscribePremium');
    const LOCK_LABEL = lockLabel(t('pricing.cta.subscribe'));
    return (
        <div className={`relative h-full rounded-2xl border p-6 ${dark ? 'bg-[#14130f] border-[#14130f]' : 'bg-white border-[#e6e3db]'}`}>
            {tier.popular && (
                <span className="absolute -top-2.5 right-5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#bef264] text-[#14130f] shadow-sm">{t('pricing.badge.popular')}</span>
            )}
            <CardHeader tier={tier} />
            {tier.highlightsIntro && (
                <p className={`text-[12px] font-bold mb-3 ${dark ? 'text-white/70' : 'text-[#14130f]'}`}>{tier.highlightsIntro}</p>
            )}
            {tier.highlights && (
                <ul className="space-y-2 mb-6">
                    {tier.highlights.map((h) => (
                        <li key={h} className={`flex items-start gap-2 text-[13px] leading-snug ${dark ? 'text-white/85' : 'text-[#14130f]'}`}>
                            <span className={`material-symbols-outlined text-[16px] shrink-0 ${dark ? 'text-[#bef264]' : 'text-[#5b53e8]'}`} style={{ marginTop: '1px' }}>check</span>
                            <span>{h}</span>
                        </li>
                    ))}
                </ul>
            )}
            {extras}
            {cta ? (
                <div>
                    {cta.note && <p className={`text-[11px] mb-2 leading-snug text-center ${dark ? 'text-white/60' : 'text-[#6b6a64]'}`}>{cta.note}</p>}
                    <CardCTA tier={tier} onSubscribe={cta.onClick} disabled={cta.disabled} neutral={cta.neutral} label={cta.label} />
                </div>
            ) : (
                <CardCTA tier={tier} onSubscribe={onSubscribe} disabled={disabled || LOCKED} label={LOCKED ? LOCK_LABEL : (tier.popular ? labelPremium : undefined)} />
            )}

        </div>
    );
}

export default function PricingSection({ onSubscribe, payLoading = false, fadeColor = '#f4f3ef', viewer = {} }) {
    const t = useT();
    const [showCompare, setShowCompare] = useState(false);

    /* Kad + baris perbandingan dibina di sini (bukan pemalar aras-modul)
       supaya labelnya ikut bahasa pilihan pelanggan. ⚠️ Kedua-dua senarai
       di bawah dipetakan dengan parameter bernama `t` (`TIERS.map((t) => …)`)
       — di DALAM map itu, `t` ialah objek pakej, BUKAN fungsi terjemahan.
       Sebab itu setiap ayat diterjemah di sini, sebelum map bermula. */
    // Tahunan = pakej Fighter. Ia keadaan KAD, bukan kad berasingan — lihat nota buildTiers.
    const [yearly, setYearly] = useState(false);
    const TIERS = buildTiers(t, yearly, <PeriodToggle yearly={yearly} onChange={setYearly} disabled={payLoading} />);
    const SECTIONS = buildSections(t, yearly);
    const CTA_SUBSCRIBE = t('pricing.cta.subscribe');
    const LOCK_LABEL = lockLabel(CTA_SUBSCRIBE);

    // ─── Add-on Flow Ultra (Sharing) +RM100 — pelanggan BARU sahaja (server tolak renewal/fighter).
    //     Tick diingati (sessionStorage) supaya kekal bila pindah /sesaat-1 → /pricing dalam domain sama;
    //     param ?flowultra=1 bawa tick merentas domain (sesaat.ai → app.sesaat.ai). ───
    const [wantFlowUltra, setWantFlowUltra] = useState(() => {
        try {
            const saved = sessionStorage.getItem('sesaat_flow_ultra');
            if (saved === '1') return true;
            if (saved === '0') return false; // user pernah untick — jangan force balik dari URL param
            return new URLSearchParams(window.location.search).get('flowultra') === '1';
        } catch (_) { return false; }
    });
    const toggleFlowUltra = () => setWantFlowUltra((v) => {
        const next = !v;
        try { sessionStorage.setItem('sesaat_flow_ultra', next ? '1' : '0'); } catch (_) { /* private mode — abaikan */ }
        return next;
    });

    // Tick add-on pada kad SMART = keadaan BERASINGAN daripada kad Premium.
    // ⛔ Satu keadaan dikongsi akan menyebabkan kedua-dua kotak bertanda serentak
    //    apabila pelanggan menekan satu — dibaca sebagai pepijat, bukan ciri.
    const [wantFlowSmart, setWantFlowSmart] = useState(false);

    // Module tambahan pakej SMART. '' = belum pilih (butang Langgan kekal mati).
    // Dibawa merentas domain melalui ?smod= sebab sesaat.ai melompat ke
    // app.sesaat.ai untuk membayar — tanpa ini pelanggan terpaksa memilih dua kali.
    const [modulSmart, setModulSmart] = useState(() => {
        try {
            const simpan = sessionStorage.getItem('sesaat_smart_modul');
            if (simpan === 'ai8saat' || simpan === 'goyang') return simpan;
            const q = new URLSearchParams(window.location.search).get('smod');
            return (q === 'ai8saat' || q === 'goyang') ? q : '';
        } catch { /* mod privasi — pilihan tidak dibawa */ return ''; }
    });
    const pilihModulSmart = (id) => {
        setModulSmart(id);
        try { sessionStorage.setItem('sesaat_smart_modul', id); } catch { /* mod privasi — pilihan hidup untuk sesi ini sahaja */ }
    };

    // ─── Bonus 50 orang terawal (public_stats/flow_bonus, baca awam) ───
    //     null = belum tahu / gagal baca → blok bonus TIDAK dipapar langsung.
    //     Nombor slot mesti benar; lebih baik senyap daripada menjanjikan angka
    //     yang kita sendiri tidak dapat sahkan.
    const [bonusAwal, setBonusAwal] = useState(null);
    useEffect(() => {
        let alive = true;
        getDoc(doc(db, 'public_stats', 'flow_bonus'))
            .then((snap) => {
                if (!alive || !snap.exists()) return;
                const d = snap.data() || {};
                if (d.active === false) return;
                const jumlah = Number(d.slotsTotal) || 0;
                const guna = Number(d.slotsUsed) || 0;
                if (jumlah <= 0) return;
                setBonusAwal({ jumlah, tinggal: Math.max(0, jumlah - guna) });
            })
            .catch(() => { /* gagal baca — bonus tidak dipapar */ });
        return () => { alive = false; };
    }, []);

    // ─── SUIS JUALAN — tutup pendaftaran pelanggan BAHARU tanpa menyentuh renew ───
    //     Suis sebenar (yang menghalang bil terjadi) ada di server: readSalesGate()
    //     dalam functions/index.js membaca doc yang SAMA. Bahagian ini paparan sahaja —
    //     supaya pelanggan nampak pintu tertutup sebelum menekan, bukan selepas.
    //     ⚠️ FAIL-OPEN: doc hilang / rules menolak / ralat rangkaian → butang kekal
    //        seperti biasa. Sengaja — paparan tidak boleh mematikan jualan sendiri;
    //        server yang menjadi penentu muktamad.
    const [salesGate, setSalesGate] = useState({ premiumNewOpen: true, fighterNewOpen: true, smartNewOpen: true });
    useEffect(() => {
        let alive = true;
        getDoc(doc(db, 'public_stats', 'sales_gate'))
            .then((snap) => {
                if (!alive || !snap.exists()) return;
                const d = snap.data() || {};
                setSalesGate({
                    premiumNewOpen: d.premiumNewOpen !== false,
                    fighterNewOpen: d.fighterNewOpen !== false,
                    smartNewOpen: d.smartNewOpen !== false,
                });
            })
            .catch(() => { /* fail-open — biar butang macam biasa */ });
        return () => { alive = false; };
    }, []);

    // ─── State matrix ikut viewer (dari App via host). Tanpa viewer = anon → semua flag false → render sedia ada. ───
    const v = viewer || {};
    const isPaidActive = v.loggedIn && v.tier === 'paid' && v.licenseStatus === 'valid';
    const isFighter = isPaidActive && v.plan === 'yearly';        // users.plan 'yearly' = Fighter
    const isPremiumActive = isPaidActive && !isFighter;           // fallback plan lain/legacy = Premium
    const isExpired = v.loggedIn && v.licenseStatus === 'expired';
    // Pelanggan pakej SMART = ada modul smartcustom TETAPI tiada wildcard '*'.
    // Sengaja dikira daripada modul, bukan medan baharu: tiada migrasi data diperlukan,
    // dan ia mustahil terpesong daripada apa yang lesen betul-betul buka.
    const modulPelanggan = v.allowedModules || [];
    // ⛔ Tamat tempoh WAJIB dikira sebagai pelanggan SMART juga. Tanpanya dia jatuh
    //    ke cabang kunci di bawah — kad SMART BERKUNCI, tiada jalan renew RM99 — dan
    //    satu-satunya butang hidup untuknya ialah 'Renew RM99' pada kad Premium,
    //    iaitu tepat lubang yang membuka SEMUA module dengan harga SMART.
    // ⛔⛔ JANGAN kenal pakej SMART daripada SENARAI MODULE. Baris ini dahulunya
    //    berbunyi "ada smartcustom dan tiada *" — andaian yang runtuh pada data
    //    sebenar: lesen Premium/Fighter yang dikeluarkan sebelum konvensyen "*"
    //    menyenaraikan keenam-enam module SATU PERSATU, termasuk smartcustom.
    //    Diukur pada pangkalan data 2026-08-31: 458 lesen — hanya 1 benar-benar
    //    pakej SMART, tetapi 163 pelanggan Premium/Fighter berbayar dilabel SMART
    //    dan dipapar butang "Naik taraf ke Premium — RM100" untuk sesuatu yang
    //    mereka SUDAH miliki. (Server menolak bil itu — jenisLesen() !== smart —
    //    jadi tiada wang tersalah caj; yang rosak ialah apa yang mereka nampak.)
    //    Kini jenis pakej dibaca daripada users.plan, sumber yang sama seperti
    //    isFighter di atas. Pagar sandaran <= 2 module hanya untuk lesen SMART lama
    //    yang users.plan-nya tidak ter-set — disahkan menangkap 1 lesen, 0 salah.
    const isSmartCustomer = (isPaidActive || isExpired)
        && !modulPelanggan.includes('*')
        && (v.plan === 'smart' || (modulPelanggan.includes('smartcustom') && modulPelanggan.length <= 2));

    // ─── Pendaftaran pelanggan BAHARU ditutup (suis jualan) ───
    //     "Baharu" = belum pernah jadi pelanggan berbayar: pelawat, akaun baharu, dan
    //     user TRIAL. Pelanggan sedia ada — aktif MAHUPUN sudah tamat tempoh — TIDAK
    //     tersentuh: cabang renew mereka di bawah kekal seperti sedia ada.
    //     ⚠️ Jangan tukar `isNewBuyer` kepada `!v.loggedIn` — user trial log masuk juga,
    //        dan merekalah kumpulan yang paling kerap menekan butang Langgan.
    //     Semasa lesen MASIH DIBACA, pelanggan sedia ada belum lagi kelihatan sebagai
    //     pelanggan — jadi kita tangguh dahulu, kalau tidak mereka nampak kilasan
    //     "Ditutup Sementara" pada butang renew mereka sendiri.
    //     ⛔ Penangguhan itu WAJIB terikat pada `loggedIn`. `licenseStatus` bermula sebagai
    //        'loading' dan bagi pelawat TANPA akaun ia tidak pernah berubah — jadi menyemak
    //        'loading' seorang diri mematikan kunci untuk SEMUA pelawat baharu, iaitu
    //        kumpulan yang paling penting disekat. (Bug ini betul-betul berlaku dan hanya
    //        tertangkap semasa ujian halaman langsung — jangan permudahkan syarat ini.)
    //     ⛔ Sesiapa yang lesennya SAH atau TAMAT TEMPOH tidak pernah dikira "baharu" — termasuk
    //        pelanggan lama yang `users.tier`-nya tidak pernah ter-set 'paid' (keadaan ini WUJUD;
    //        lihat nota "Sandaran licenseStatus" dalam App.jsx). Bagi mereka `isPaidActive` palsu
    //        FALSE, jadi tanpa pagar `licenseStatus === 'valid'` di bawah mereka akan nampak butang
    //        BERKUNCI dan tidak boleh menyambung langganan langsung. Kunci jualan ini tidak
    //        sekali-kali boleh menghalang renew — itu duit masuk, bukan pelanggan baharu.
    // ⛔⛔ DUA PEPIJAT JUALAN DIBAIKI DI SINI (2026-08-29) — baca sebelum menyentuh.
    //
    // (1) TUNGGU SESI SELESAI DIBACA. Firebase memulihkan sesi dari storan browser
    //     secara TAK SEGERA. Sebelum ia siap, `loggedIn` palsu FALSE — jadi halaman
    //     menganggap SETIAP pelawat berulang sebagai orang baharu dan memapar kotak
    //     add-on + bonus + harga RM299. Beberapa ratus milisaat kemudian sesi masuk
    //     dan semuanya DITARIK BALIK depan mata pelanggan (butang Log In bertukar
    //     Dashboard pada saat sama). Pelanggan membacanya sebagai "halaman refresh,
    //     tawaran hilang" — disahkan daripada video rakaman pelanggan sebenar.
    //     `authLoading` datang daripada App.jsx dan menutup tingkap itu.
    //     ⚠️ Kesannya kotak lambat muncul ~sekejap untuk pelawat tulen. Itu SENGAJA:
    //        lebih baik lambat daripada menjanjikan sesuatu lalu menariknya semula.
    //
    // (2) PENGGUNA FREE TRIAL IALAH PEMBELI BAHARU. validateLicense memulangkan
    //     status 'valid' untuk akaun trial (supaya mereka boleh masuk app), dan
    //     baris di bawah dahulunya membaca 'valid' itu sebagai "sudah jadi pelanggan"
    //     — jadi orang yang sedang mencuba percuma, iaitu kumpulan yang PALING
    //     mungkin membeli, adalah satu-satunya kumpulan yang tidak nampak add-on,
    //     bonus, mahupun promo 50 orang terawal. Nota di atas fungsi ini sejak dulu
    //     sudah menyatakan trial = baharu; pelaksanaannya yang tersasar.
    //     ⛔ JANGAN buang `!isTrial` — tanpa ia promo mati semula untuk trial.
    const isTrial = v.tier === 'trial';
    const licenseSettling = !!v.authLoading || (!!v.loggedIn && (!v.licenseStatus || v.licenseStatus === 'loading'));
    const isExistingCustomer = !!v.loggedIn && !isTrial && (isPaidActive || isExpired || v.licenseStatus === 'valid');
    const isNewBuyer = !isExistingCustomer && !licenseSettling;
    const premiumNewClosed = isNewBuyer && !salesGate.premiumNewOpen;
    const fighterNewClosed = isNewBuyer && !salesGate.fighterNewOpen;
    const smartNewClosed = isNewBuyer && !salesGate.smartNewOpen;
    // Kad "popular" bertukar identiti ikut toggle, jadi pintu yang berkaitan pun bertukar.
    const popularClosed = yearly ? fighterNewClosed : premiumNewClosed;
    const CLOSED_LABEL = lockLabel(t('pricing.gate.closedLabel'));
    const CLOSED_NOTE = t('pricing.gate.closedNote');

    // Override CTA per pakej — null = behavior sedia ada (anon/trial/loading/admin tak berubah).
    const ctaFor = (tier) => {
        // Pintu ditutup → butang berkunci, didahulukan sebelum semua cabang lain.
        // Satu tempat sahaja: ctaFor dipakai oleh KEEMPAT-EMPAT titik render (kad
        // desktop, kad mobile, baris CTA table desktop, baris CTA table mobile),
        // jadi tiada laluan tertinggal terbuka.
        // ── Kad SMART ──
        if (tier.planKey === 'smart') {
            if (isSmartCustomer) return {
                label: LOCKED ? lockLabel(t('pricing.cta.renew')) : t('pricing.cta.renewSmart'),
                disabled: payLoading || LOCKED,
                onClick: () => sub('smart'),
            };
            // Pelanggan Premium/Fighter (aktif atau tamat tempoh) tidak boleh turun ke SMART —
            // server pun menolaknya, jadi butang di sini dikunci supaya mereka tidak mencuba.
            if (isPaidActive || isExpired) return { label: LOCK_LABEL, disabled: true };
            if (smartNewClosed) return { label: CLOSED_LABEL, disabled: true, note: CLOSED_NOTE };
            // Label dikira di sini (bukan dalam buildTiers) kerana tick add-on
            // hanya wujud selepas senarai pakej dibina.
            const hargaSmart = flowSmartTicked ? 199 : 99;
            // Belum pilih module → butang MATI + sebabnya dinyatakan. Butang yang
            // menolak tanpa memberitahu sebab dibaca sebagai butang rosak.
            if (perluPilihModul && !modulSmart) {
                return {
                    label: LOCKED ? LOCK_LABEL : t('pricing.cta.subscribePrice', { harga: hargaSmart }),
                    disabled: true,
                    note: t('pricing.smartPick.wajib'),
                };
            }
            return {
                label: LOCKED ? LOCK_LABEL : t('pricing.cta.subscribePrice', { harga: hargaSmart }),
                disabled: payLoading || LOCKED,
                onClick: () => sub('smart'),
            };
        }
        // ── Pelanggan SMART melihat kad Premium → laluan naik taraf RM100 ──
        if (tier.id === 'pro' && isSmartCustomer) {
            // Lesen SMART sudah TAMAT → laluan naik taraf RM100 ditolak server (ia
            // menuntut lesen aktif), dan server mencaj harga MASUK penuh. Label di
            // sini mesti padan dengan apa yang server benar-benar caj.
            if (!isPaidActive) {
                return {
                    label: LOCKED ? LOCK_LABEL : t('pricing.cta.subscribePrice', { harga: yearly ? 799 : 199 }),
                    disabled: payLoading || LOCKED,
                    onClick: () => sub(tier.planKey),
                };
            }
            return {
                label: t('pricing.cta.upgradeSmart'),
                disabled: payLoading || LOCKED,
                onClick: () => sub('smart_upgrade'),
                note: t('pricing.cta.upgradeSmartNote'),
            };
        }
        if (tier.planKey === 'premium' && premiumNewClosed) {
            return { label: CLOSED_LABEL, disabled: true, note: CLOSED_NOTE };
        }
        if (tier.planKey === 'fighter' && fighterNewClosed) {
            return { label: CLOSED_LABEL, disabled: true, note: CLOSED_NOTE };
        }
        if (tier.planKey === 'premium') {
            // Premium aktif yang DAH ada FLOWNITY → renew sahaja (takda add-on nak dibeli).
            if (isPremiumActive) return {
                label: LOCKED ? lockLabel(t('pricing.cta.renew')) : t('pricing.cta.renewPrice'),
                disabled: payLoading || LOCKED,
                onClick: () => sub('premium'),
            };
            if (isFighter) return { label: LOCK_LABEL, disabled: true }; // Fighter tak perlu beli Premium
            if (isExpired) return {
                label: LOCKED ? lockLabel(t('pricing.cta.renew')) : t('pricing.cta.renewPrice'),
                disabled: payLoading || LOCKED,
                onClick: () => sub('premium'),
            };
        }
        if (tier.planKey === 'fighter' && isFighter) return { label: t('pricing.cta.currentPlan'), disabled: true, neutral: true };
        return null;
    };

    // Slot extras pada kad Premium (hitam).
    // Bila tawaran combo DITUTUP, setiap laluan jualan add-on (selector RM69/RM168 + kad bundle)
    // diganti kotak BERKUNCI "Telah Tamat" — arahan user 2026-08-03: papar, JANGAN sorok.
    // Pelanggan yang DAH ada FLOWNITY kekal nampak nota ✓ (mereka tak kehilangan apa-apa).
    // Kotak Triple Combo DIBUANG SEPENUHNYA 2026-08-28 (arahan user). Tawaran itu
    // tamat 31 Julai, tetapi kotaknya masih dipapar kepada SETIAP pelanggan sedia
    // ada — termasuk orang yang cuma mahu menekan Renew. Yang tinggal di sini
    // hanyalah nota MAKLUMAT: ia memberitahu pelanggan apa yang dia sudah ada,
    // bukan menawarkan apa-apa untuk dibeli.
    // ⛔ NOTA STATUS AKAUN DIBUANG 2026-08-28 (arahan user). Dua nota lama
    //    ("Semua modul dibuka untuk Fighter", "Add-on FLOWNITY telah diaktifkan")
    //    bercakap tentang apa yang pelanggan sudah langgan — dan ia MENGELIRUKAN:
    //    pemegang FLOWNITY sebenarnya turut memiliki SMART dan GOYANG, kerana promo
    //    Triple Combo lama memberi KETIGA-TIGA module sekaligus. Menyebut satu
    //    sahaja membuat pelanggan menyangka dia kurang daripada yang sebenar.
    //    Halaman harga bercakap tentang APA YANG DIJUAL, bukan status akaun.
    let premiumExtras = null;

    // Kotak Flow Ultra hanya untuk pembeli BARU (anon/trial) — server memang tolak add-on ni
    // untuk pelanggan aktif/renewal.
    // ⛔ JANGAN kembalikan syarat lama `premiumExtras === null`. Sejak kotak combo dikunci,
    //    laluan renew/tamat-tempoh boleh berakhir tanpa extras → kotak Flow Ultra akan BOCOR
    //    kepada pelanggan renew yang langsung tak layak membelinya.
    // ⛔ Bila pendaftaran baharu ditutup, kotak ni WAJIB hilang sekali: add-on Flow Ultra
    //    +RM100 hanya boleh dibeli bersama pembelian Premium BAHARU (server tolak untuk
    //    renewal/fighter). Membiarkannya = tick yang menjanjikan sesuatu yang tak boleh dibeli.
    // Add-on kini dibuka untuk Fighter juga (arahan user 2026-08-26) — sebab itu ia
    // berpaut pada popularClosed, bukan premiumNewClosed sahaja.
    const showFlowUltra = isNewBuyer && !popularClosed;
    const flowUltraTicked = showFlowUltra && wantFlowUltra;
    if (showFlowUltra) {
        premiumExtras = <FlowUltraAddonBox checked={wantFlowUltra} onToggle={toggleFlowUltra} bonus={bonusAwal} />;
    }

    // Add-on juga ditawarkan pada pakej SMART (arahan user 2026-08-27).
    // Pintu jualannya ialah pintu SMART, bukan pintu Premium.
    const showFlowSmart = isNewBuyer && !smartNewClosed;
    const flowSmartTicked = showFlowSmart && wantFlowSmart;
    // ⭐ Pemilih module DIPAPAR KEPADA SEMUA JENIS PELAWAT (arahan user 2026-08-28).
    //    Ia sebahagian daripada apa yang dijual, jadi ia patut kelihatan walaupun
    //    kepada orang yang tidak boleh menekannya sekarang.
    // ⛔ TETAPI ia hanya BOLEH DITEKAN oleh pembeli baharu. Pelanggan SMART yang
    //    menyambung langganan mengekalkan module asalnya — server MENGABAIKAN
    //    pilihan module pada renew (pagar dalam createBillplzBill), jadi butang
    //    yang boleh ditekan di sini ialah butang yang tidak mengubah apa-apa.
    const perluPilihModul = isNewBuyer && !smartNewClosed;
    // Module yang pelanggan SMART sedang pegang — ditanda supaya kotak ini
    // memberitahu dia sesuatu yang benar, bukan sekadar kotak kosong.
    const modulSmartSemasa = modulPelanggan.find((m) => m === 'ai8saat' || m === 'goyang') || '';
    const smartExtras = (
        <>
            <PilihModulSmart
                value={perluPilihModul ? modulSmart : modulSmartSemasa}
                onChange={pilihModulSmart}
                bacaSahaja={!perluPilihModul}
                milikSaya={isSmartCustomer}
            />
            {showFlowSmart && <FlowUltraAddonBox checked={wantFlowSmart} onToggle={() => setWantFlowSmart((v) => !v)} bonus={bonusAwal} />}
        </>
    );
    // ⛔ BONUS = TAWARAN PEMBELI BAHARU, bukan isi pakej (keputusan user 2026-08-28).
    //    Sebelum ini ia dipapar kepada semua — termasuk pelanggan yang lesennya sudah
    //    tamat dan cuma mahu menekan Renew. Dia nampak senarai hadiah yang bukan
    //    untuknya, dan itu menjanjikan sesuatu yang dia takkan terima.
    // ⛔ Item "Tools Auto Threads & Facebook Group" DIBUANG — ia produk yang SAMA
    //    dengan Toukey Viral, jadi menyenaraikan kedua-duanya mengira satu hadiah
    //    sebagai dua.
    if (isNewBuyer) {
        premiumExtras = (
            <>
                <BonusBox items={[
                    { teks: t('pricing.bonus.boost') },
                    { teks: t('pricing.bonus.toukey'), href: 'https://toukeyviral.my/ait.fighter?ref=sesaat', logo: '/bonus/toukey.jpg' },
                ]} />
                {premiumExtras}
            </>
        );
    }
    const hargaPopular = yearly ? (flowUltraTicked ? 899 : 799) : (flowUltraTicked ? 299 : 199);
    const premiumLabel = LOCKED ? LOCK_LABEL : t('pricing.cta.subscribePrice', { harga: hargaPopular });

    // Hantar planKey + pilihan add-on ke host. Host lama yang abaikan arg ke-2 kekal selamat
    // (SalesPageView cuma navigate ke /pricing — tick dibawa via sessionStorage).
    function sub(planKey) {
        // Setiap pakej menghantar TICK MILIKNYA sendiri — kad SMART dan kad
        // Premium ada kotak berasingan, jadi tick satu tidak boleh menaikkan
        // harga pakej yang satu lagi.
        const mahuAddon = planKey === 'smart'
            ? flowSmartTicked
            : ((planKey === 'premium' || planKey === 'fighter') && flowUltraTicked);
        if (onSubscribe) onSubscribe(planKey, { wantFlowAddon: mahuAddon, smartModule: planKey === 'smart' ? modulSmart : '' });
    }

    return (
        <div className="mt-12">

            {/* ─── DESKTOP: peek (kad + table), fade sampai ~Output video/hari ─── */}
            <div className="hidden md:block relative">
                {/* ⚠️ Tinggi peek MESTI muat kad PENUH termasuk butang Langgan. Nilai lama
                    1260px ditala untuk kad pendek; selepas pemilih module + kotak add-on +
                    kotak bonus ditambah (Ogos 2026) kad jadi ~1570px dan butang beli
                    tersembunyi di bawah fade — pelawat desktop tidak nampak jalan membayar.
                    Naikkan nilai ini setiap kali kandungan kad bertambah. */}
                <div className={showCompare ? '' : 'max-h-[1780px] overflow-hidden'}>
                    <div className="overflow-x-auto">
                        <div className="relative isolate min-w-[760px]">

                            {/* Panel highlight kolum Premium */}
                            <div className="absolute top-4 bottom-0 z-0 rounded-2xl pointer-events-none bg-black/[0.03]" style={{ left: '70%', width: '30%' }} />

                            {/* Kad pakej — grid */}
                            <div className="relative z-10 grid items-stretch pt-4 mb-2" style={{ gridTemplateColumns: '40% 30% 30%' }}>
                                <div />
                                {TIERS.map((t) => (
                                    <div key={t.id} className="px-1.5 h-full">
                                        <TierCard tier={t} onSubscribe={() => sub(t.planKey)} disabled={payLoading} cta={ctaFor(t)} extras={t.popular ? premiumExtras : smartExtras} premiumLabel={premiumLabel} />
                                    </div>
                                ))}
                            </div>

                            {/* Table perbandingan */}
                            <table className="relative z-10 w-full table-fixed border-collapse">
                                <colgroup>
                                    <col style={{ width: '40%' }} />
                                    <col style={{ width: '30%' }} />
                                    <col style={{ width: '30%' }} />
                                </colgroup>
                                <tbody>
                                    {SECTIONS.map((section) => (
                                        <React.Fragment key={section.title}>
                                            <tr>
                                                <td colSpan={3} className="pt-7 pb-3">
                                                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#9c9a92]">{section.title}</span>
                                                </td>
                                            </tr>
                                            {section.rows.map((row) => (
                                                <tr key={row.label} className="border-t border-[#e6e3db]">
                                                    <td className="py-3 pr-4">
                                                        <span className="text-sm font-medium text-[#14130f]">{row.label}</span>
                                                    </td>
                                                    {row.vals.map((v, ci) => (
                                                        <td key={ci} className="py-3 text-center">
                                                            <ValCell v={v} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}

                                    {/* Baris CTA bawah — ikut state matrix sama macam kad atas */}
                                    <tr className="border-t border-[#e6e3db]">
                                        <td className="pt-6"></td>
                                        {TIERS.map((t) => {
                                            const cta = ctaFor(t);
                                            return (
                                                <td key={t.id} className="pt-6 px-1.5 align-top">
                                                    <button
                                                        onClick={cta ? cta.onClick : () => sub(t.planKey)}
                                                        disabled={cta ? cta.disabled : (payLoading || LOCKED)}
                                                        className={`w-full h-10 rounded-xl text-sm font-bold transition-colors ${
                                                            cta?.neutral
                                                                ? 'border border-[#e6e3db] bg-[#f4f3ef] text-[#9c9a92] cursor-default'
                                                                : `disabled:opacity-50 disabled:cursor-not-allowed ${
                                                                    t.popular
                                                                        ? 'bg-[#14130f] hover:bg-[#2a2823] text-white'
                                                                        : 'border border-[#e6e3db] text-[#14130f] bg-white hover:bg-[#faf9f5]'
                                                                }`
                                                        }`}
                                                    >
                                                        {cta ? cta.label : (t.popular ? premiumLabel : (LOCKED ? LOCK_LABEL : (t.cta || CTA_SUBSCRIBE)))}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                {!showCompare && (
                    <div
                        className="absolute inset-x-0 bottom-0 h-56 pointer-events-none"
                        style={{ background: `linear-gradient(to top, ${fadeColor} 12%, transparent 100%)` }}
                    />
                )}
            </div>

            {/* ─── MOBILE: kad swipe sebelah-sebelah — kad ke-2 menjenguk di tepi (snap carousel).
                 pt-4 bagi ruang badge POPULAR (container scroll clip overflow menegak). ─── */}
            <div className="md:hidden flex gap-3 overflow-x-auto snap-x snap-mandatory pt-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {TIERS.map((t) => (
                    <div key={t.id} className="w-[86%] shrink-0 snap-center">
                        <MobileTierCard tier={t} onSubscribe={() => sub(t.planKey)} disabled={payLoading} cta={ctaFor(t)} extras={t.popular ? premiumExtras : smartExtras} premiumLabel={premiumLabel} />
                    </div>
                ))}
            </div>

            {/* ─── MOBILE: table perbandingan gaya desktop (bg cerah) — peek sampai habis seksyen Modul
                 + fade, penuh bila "Bandingkan". Kad carousel tak sejajar kolum, jadi table ada baris
                 nama pakej sendiri. ─── */}
            <div className="md:hidden relative mt-6">
                <div className={showCompare ? '' : 'max-h-[430px] overflow-hidden'}>
                    <div className="relative isolate">

                        {/* Panel highlight kolum Premium */}
                        <div className="absolute top-0 bottom-0 z-0 rounded-2xl pointer-events-none bg-black/[0.03]" style={{ left: '72%', width: '28%' }} />

                        <table className="relative z-10 w-full table-fixed border-collapse">
                            <colgroup>
                                <col style={{ width: '44%' }} />
                                <col style={{ width: '28%' }} />
                                <col style={{ width: '28%' }} />
                            </colgroup>
                            <tbody>
                                <tr>
                                    <td className="pt-3"></td>
                                    {TIERS.map((t) => (
                                        <td key={t.id} className="pt-3 pb-1 text-center">
                                            <span className="text-[13px] font-bold text-[#14130f]">{t.name}</span>
                                        </td>
                                    ))}
                                </tr>
                                {SECTIONS.map((section) => (
                                    <React.Fragment key={section.title}>
                                        <tr>
                                            <td colSpan={3} className="pt-6 pb-2.5">
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-[#9c9a92]">{section.title}</span>
                                            </td>
                                        </tr>
                                        {section.rows.map((row) => (
                                            <tr key={row.label} className="border-t border-[#e6e3db]">
                                                {/* whitespace-normal: CSS global set td nowrap (table admin) — kolum mobile sempit, wajib benarkan wrap */}
                                                <td className="py-3 pr-2 whitespace-normal">
                                                    <span className="text-[13px] font-medium text-[#14130f]">{row.label}</span>
                                                </td>
                                                {row.vals.map((v, ci) => (
                                                    <td key={ci} className="py-3 px-1 text-center whitespace-normal">
                                                        <ValCell v={v} />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}

                                {/* Baris CTA bawah — ikut state matrix sama macam desktop */}
                                <tr className="border-t border-[#e6e3db]">
                                    <td className="pt-5"></td>
                                    {TIERS.map((t) => {
                                        const cta = ctaFor(t);
                                        return (
                                            <td key={t.id} className="pt-5 px-1 align-top">
                                                <button
                                                    onClick={cta ? cta.onClick : () => sub(t.planKey)}
                                                    disabled={cta ? cta.disabled : (payLoading || LOCKED)}
                                                    className={`w-full h-9 rounded-xl text-xs font-bold transition-colors ${
                                                        cta?.neutral
                                                            ? 'border border-[#e6e3db] bg-[#f4f3ef] text-[#9c9a92] cursor-default'
                                                            : `disabled:opacity-50 disabled:cursor-not-allowed ${
                                                                t.popular
                                                                    ? 'bg-[#14130f] hover:bg-[#2a2823] text-white'
                                                                    : 'border border-[#e6e3db] text-[#14130f] bg-white hover:bg-[#faf9f5]'
                                                            }`
                                                    }`}
                                                >
                                                    {cta ? cta.label : (t.popular ? premiumLabel : (LOCKED ? LOCK_LABEL : (t.cta || CTA_SUBSCRIBE)))}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                {!showCompare && (
                    <div
                        className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
                        style={{ background: `linear-gradient(to top, ${fadeColor} 12%, transparent 100%)` }}
                    />
                )}
            </div>

            <div className="flex justify-center mt-7">
                <button
                    onClick={() => setShowCompare((v) => !v)}
                    className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full border border-[#e6e3db] bg-white text-sm font-bold text-[#14130f] hover:bg-[#faf9f5] transition-colors"
                >
                    {showCompare ? t('pricing.compare.hide') : t('pricing.compare.show')}
                    <span className="material-symbols-outlined text-[18px]">{showCompare ? 'expand_less' : 'expand_more'}</span>
                </button>
            </div>
        </div>
    );
}
